import { useCompanionStore } from "@/stores/companion-store";
/** Hub routing, agent metadata and canvas presentation actions. */
import {
  openFrameInProject,
  openTaskInProject,
} from "@/components/layout/cross-project-navigation";
import type { HubNavigationHandlers } from "@/components/layout/global-search-navigation";
import { openMemoryNode } from "@/components/layout/memory-node-navigation";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { showAllFramesTidied } from "@/lib/canvas-presentation";
import { fitCanvasTimePage } from "@/lib/canvas-time-page-actions";
import { resolveDenkerProjectAgent } from "@/lib/denker-composer-agent";
import { toggleFocusPresentationMode } from "@/lib/focus-presentation";
import { resolveTemplateModel } from "@/lib/model-registry";
import { selectProjectByIdentity } from "@/lib/select-project-space";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useReviewModeStore } from "@/stores/review-mode-store";
import { useCallback, useEffect, useMemo } from "react";
import type { useMasterLayoutMedia } from "./use-master-layout-media";
import type { useMasterLayoutShell } from "./use-master-layout-shell";

export function useMasterLayoutNavigation(
  shell: ReturnType<typeof useMasterLayoutShell>,
  media: ReturnType<typeof useMasterLayoutMedia>,
) {
  const { data, layout, handleOpenSettingsForAgent } = shell;
  const { setScreenshotIntent } = media;
  const hubNavigation = useMemo<HubNavigationHandlers>(
    () => ({
      openProject: (projectId) => {
        void selectProjectByIdentity({ projectId });
      },
      openFrame: (frame) => {
        void openFrameInProject(frame);
      },
      openTask: (task) => {
        void openTaskInProject(task);
      },
      openAgent: (agent) => {
        void (async () => {
          const isCurrent = captureDesktopChatAuthScopeGuard();
          if (
            agent.projectId &&
            !(await selectProjectByIdentity(
              { projectId: agent.projectId },
              { shouldContinue: isCurrent },
            ))
          )
            return;
          if (isCurrent()) handleOpenSettingsForAgent(agent.templateId);
        })();
      },
      openMemory: (nodeId) =>
        openMemoryNode({
          nodeId,
          currentProject: data.currentProject,
          currentSpace: data.currentSpace,
          selectFrame: data.handleSelectFrame,
        }),
      openSetting: (page) => layout.openSettings({ page }),
    }),
    [
      data.currentProject,
      data.currentSpace,
      data.handleSelectFrame,
      handleOpenSettingsForAgent,
      layout,
    ],
  );

  const panToFrame = useCanvasStore((s) => s.panToFrame);

  // Agent store reads — hoisted so slashCommands can include the
  // currently-selected agent's skills.
  const agentTemplates = useAgentStore((s) => s.templates);
  const projectAgents = useAgentStore((s) => s.agents);
  const skillRegistry = useAgentStore((s) => s.skills);
  const fetchAllTemplates = useAgentStore((s) => s.fetchAllTemplates);
  const fetchSkills = useAgentStore((s) => s.fetchSkills);
  useEffect(() => {
    if (agentTemplates.length === 0) {
      void fetchAllTemplates();
    }
  }, [agentTemplates.length, fetchAllTemplates]);
  useEffect(() => {
    if (skillRegistry.length === 0) {
      void fetchSkills();
    }
  }, [skillRegistry.length, fetchSkills]);

  // Screenshots must use the same model as the Denker chat recipient.
  const resolveModelForCapture = useCallback((): string | undefined => {
    const denker = resolveDenkerProjectAgent(projectAgents);
    const template = agentTemplates.find(
      (item) => item.id === denker?.agent_template,
    );
    return resolveTemplateModel(
      template,
      useCompanionStore.getState().availableModels,
    ).model;
  }, [projectAgents, agentTemplates]);

  const summonBarOpen = useCanvasStore(
    (s) => !!(s.pendingCursorSummon || s.cursorInput),
  );
  const summonSelectionPresent = useCanvasStore(
    (s) => !!(s.pendingCursorSummon?.selection ?? s.cursorInput?.selection),
  );
  useEffect(() => {
    // Bar dismissed — clear this request's capture intent. Screenshots
    // are captured only by the submit handler, so switching windows after
    // opening the summon bar cannot send stale screen context.
    if (summonBarOpen) return;
    setScreenshotIntent(false);
  }, [summonBarOpen, setScreenshotIntent]);

  const runTidy = useCallback(() => {
    showAllFramesTidied();
  }, []);

  const handleFitView = useCallback(() => {
    fitCanvasTimePage(undefined, { tidyFrameLayout: true });
  }, []);

  const handleReviewMode = useCallback(() => {
    const reviewMode = useReviewModeStore.getState();
    if (
      reviewMode.isOpen ||
      reviewMode.loading ||
      usePresentationStore.getState().presentation?.variant === "review"
    ) {
      reviewMode.closeReviewMode();
      return;
    }
    void reviewMode.openReviewMode();
  }, []);

  return {
    hubNavigation,
    panToFrame,
    agentTemplates,
    projectAgents,
    skillRegistry,
    resolveModelForCapture,
    summonSelectionPresent,
    runTidy,
    handleFitView,
    handleFocusMode: toggleFocusPresentationMode,
    handleReviewMode,
  };
}
